import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import type { ComponentProps } from 'react'
import { useContext, useMemo } from 'react'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import { SiteTakeoverCampaignTagLabel } from 'takeovers/utils/components/SiteTakeoverCampaignTagLabel'
import { LUX_PLUS_MONTH_CAMPAIGN_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import BedbankOfferCardPriceDetails from './BedbankOfferCardPriceDetails'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import useBedbankRates from 'hooks/useBedbankRates'
import { useOfferRatePrice } from 'hooks/Offers/useOfferPriceNew'
import { useRebrandOverlayBookmarkProps } from 'hooks/useRebrandOverlayButtonProps'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import { generateLoyaltyPointsCalculatorBedbankOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import GrowthCarouselCardSmall from './GrowthOfferCard/GrowthCarouselCardSmall'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import useGrowthOfferTile from './GrowthOfferCard/useGrowthOfferTile'

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends InheritedCarouselCardMediumProps {
  offer: App.BedbankOffer | App.BedbankOfferSummary | App.BedbankOfferWithRate
  onClick: (isRightClick?: boolean) => void
  className?: string
  offerUrl: string
  size: 'small' | 'medium'
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  filters?: App.OfferListFilters
  PricingComponent?: React.ComponentType
  CtaComponent?: React.ComponentType
}

function BedbankOfferCard(props: Props) {
  const isGrowthTile = useGrowthOfferTile()
  const {
    offer,
    onClick,
    className,
    offerUrl,
    size,
    selectedDatesAndRoomsFilter,
    filters,
    variant,
    eagerLoadImage,
    CtaComponent,
    PricingComponent,
  } = props

  const { currentRegionCode } = useContext(GeoContext)
  const { iconKind, iconVariant } = useRebrandOverlayBookmarkProps()

  const location = [
    offer.property.address.city,
    offer.property.address.countryName,
  ]
    .filter(Boolean)
    .join(', ')

  const [{ hotelOnlyRates }] = useBedbankRates(offer.id)
  const rate = hotelOnlyRates[0]
  const { default: pricing, member: memberPricing } = useOfferRatePrice(offer, rate)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    const propertyFees = pricing?.fees?.find((fee) => fee.type === 'property')?.amount ?? 0
    return [
      generateLoyaltyPointsCalculatorBedbankOptions(offer, rate, {
        price: pricing?.price,
        memberPrice: memberPricing?.price,
        propertyFees,
        currentRegionCode,
      }),
    ]
  }, [memberPricing?.price, pricing?.price, rate, pricing?.fees, offer, currentRegionCode])

  const tileBurn = useSearchOfferBurnPricing({
    price: pricing?.price ?? 0,
    memberPrice: memberPricing?.price,
    loyaltyProductType: getLoyaltyProductType(offer),
    mx: rate?.mx,
    viewPriceInPoints: !!filters?.viewPriceInPoints,
    disabled: !pricing?.price,
  })

  // Only show property name if different from offer name to avoid duplication
  const providerName = offer.property.name !== offer.name ? offer.property.name : undefined

  if (isGrowthTile) {
    return (
      <ProductPaletteProvider palette="default">
        <GrowthCarouselCardSmall
          variant={variant}
          data-testid="offer-card"
          className={className}
          eagerLoadImage={eagerLoadImage}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="never" size="small" iconKind={iconKind} iconVariant={iconVariant} />}
          image={offer.image}
          images={offer.images}
          location={location}
          providerName={providerName}
          title={offer.name}
          description={offer.amenities}
          urgencyLabels={<LabelGroup>
            {offer.campaignTags?.filter(campaignTag => campaignTag === LUX_PLUS_MONTH_CAMPAIGN_ID).map(campaignTag => <SiteTakeoverCampaignTagLabel key={campaignTag} campaignTagId={campaignTag} onOfferCard offer={offer} />)}
          </LabelGroup>}
          priceDetails={<BedbankOfferCardPriceDetails variant="default" offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} filters={filters} CtaComponent={CtaComponent} PricingComponent={PricingComponent} />}
          to={offerUrl}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          onClick={onClick}
        />
      </ProductPaletteProvider>
    )
  }

  return (
    <ProductPaletteProvider palette="default">
      {size === 'medium' && <CarouselCardMedium
        variant={variant}
        data-testid="offer-card"
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
        image={offer.image}
        images={offer.images}
        location={location}
        title={offer.name}
        providerName={providerName}
        priceDetails={<Group direction="vertical" gap={4}>
          {offer.campaignTags?.filter(campaignTag => campaignTag === LUX_PLUS_MONTH_CAMPAIGN_ID).map(campaignTag => <SiteTakeoverCampaignTagLabel key={campaignTag} campaignTagId={campaignTag} onOfferCard offer={offer} />)}
          <BedbankOfferCardPriceDetails variant="condensed" offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} filters={filters} CtaComponent={CtaComponent} PricingComponent={PricingComponent} />
        </Group>}
        to={offerUrl}
        rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
        onClick={onClick}
        loyaltyPointsElement={<LoyaltyPointsEarn
          calculationRequests={pointsEarnCalculationRequests}
          calculationType="estimate"
          offer={offer}
          pointsToApply={tileBurn.pointsToApplyForEarn}
          burnPriceInPoints={tileBurn.priceInPointsForEarn}
        />}
      />}
      {size === 'small' && <CarouselCardSmall
        variant={variant}
        data-testid="offer-card"
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
        description={offer.amenities}
        descriptionLines={2}
        image={offer.image}
        images={offer.images}
        location={location}
        title={offer.property.name}
        priceDetails={<Group direction="vertical" gap={4}>
          {offer.campaignTags?.filter(campaignTag => campaignTag === LUX_PLUS_MONTH_CAMPAIGN_ID).map(campaignTag => <SiteTakeoverCampaignTagLabel key={campaignTag} campaignTagId={campaignTag} onOfferCard offer={offer} />)}
          <BedbankOfferCardPriceDetails offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} filters={filters} CtaComponent={CtaComponent} PricingComponent={PricingComponent} />
        </Group>}
        to={offerUrl}
        rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
        onClick={onClick}
        loyaltyPointsElement={<LoyaltyPointsEarn
          calculationRequests={pointsEarnCalculationRequests}
          calculationType="estimate"
          offer={offer}
          pointsToApply={tileBurn.pointsToApplyForEarn}
          burnPriceInPoints={tileBurn.priceInPointsForEarn}
        />}
      />}
    </ProductPaletteProvider>
  )
}

export default BedbankOfferCard
